import axios from 'axios';
import router from '@/router'; // Ensure correct router import
import { isPwaUpdateCooldown } from '@/utils/pwaUpdate';

// Create an Axios instance
const instance = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || 'http://pixiev2:3000',
  // Prevent hung requests (e.g. flaky mobile networks) from freezing route guards
  timeout: 20000,
});

// Function to get the token from local storage
const getAuthToken = () => {
  return localStorage.getItem('token');
};

// Add a request interceptor to include the Authorization header
instance.interceptors.request.use(
  (config) => {
    // Set the Authorization header with the token for all requests
    const token = getAuthToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    // Handle any request errors here
    return Promise.reject(error);
  },
);

// Add a response interceptor to handle errors globally except 401, 404, and 500
instance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Check if error.response is available (indicating an HTTP error)
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      router.push({ name: 'login' });
      return Promise.reject(error);
    }

    if (error.config && error.config.skipGlobalError) {
      return Promise.reject(error);
    }

    if (error.response) {
      // Skip dispatching CustomEvent for 404 and 500 errors
      if (error.response.status === 404 || error.response.status === 500) {
        return Promise.reject(error); // Stop further processing
      }

      // Dispatch CustomEvent for errors excluding 401, 404, and 500
      if (error.response.status >= 400) {
        window.dispatchEvent(
          new CustomEvent('global-error', { detail: error }),
        );
      }
    } else if (error.code === 'ERR_NETWORK') {
      if (isPwaUpdateCooldown()) {
        return Promise.reject(error);
      }
      // Handle network errors (like the one you're experiencing)
      window.dispatchEvent(
        new CustomEvent('global-error', {
          detail: {
            message: '<p>Network Error: Could not connect to the server.</p>',
            stack: error.stack || 'No stack trace available',
          },
        }),
      );
    }

    return Promise.reject(error);
  },
);

export default instance;
